import {Link, useNavigate} from "react-router";
import {ChangeEvent, useState} from "react";
import { useWishList } from "../pages/wishlist/useWishList";
import { ProductType, UserType, UseWishListReturnType, WishListItemType } from "../types";
import { Loading } from "../utils";
import { useCart } from "../pages/cart/useCart";
import { useAppState } from "../context";

export const WishListTable = () => {

    const {wishlist, loading, removeFromWishlist}:UseWishListReturnType = useWishList();
    const { updateCartItem, isInCart } = useCart();
    const [selectedWishItems, setSelectedWishItems] = useState<WishListItemType[]>([]);
    const [allSelected, setAllSelected] = useState(false);
    const [user] = useAppState<UserType>('user');
    const navigate = useNavigate();
    const formatDate = (date: Date): string => {
        return new Intl.DateTimeFormat('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        }).format(date);
    };

    const removeUnavailable = () => {
        wishlist.filter(v => v.product.isAvailable).forEach(v => {
            removeFromWishlist(v.product)
        })
    }

    const removeAll = () => {
        wishlist.forEach(v => {
            removeFromWishlist(v.product)
        })
    }

    const onWishItemCheckboxChange = (event: ChangeEvent<HTMLInputElement>, item: WishListItemType) => {
        event.stopPropagation();
        const isChecked = event.target.checked;
        
        const newSelectedItems = isChecked 
            ? [...selectedWishItems, item]
            : selectedWishItems.filter(v => v.product.id !== item.product.id);
        
        setSelectedWishItems(newSelectedItems);
        setAllSelected(newSelectedItems.length === wishlist.length);
    };

    const addToCart = (products: ProductType[]) => {
        products.forEach(product => {
            if (!isInCart(product) && product.isAvailable) {
                updateCartItem({
                    product: product.id,
                    quantity: 1,
                    additionalPhotoCount: 0,
                    giftVideoStory: false,
                    images: [],
                });
            }
        });
    };

    const addSelectedToCart = () => {
        addToCart(selectedWishItems.map(item => item.product));
        navigate(user ? '/account/cart' : '/cart');
    };

    const addAllToCart = () => {
        addToCart(wishlist.map(item => item.product));
        navigate(user ? '/account/cart' : '/cart');
    };

    const toggleAllItems = (event: ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();
        const isChecked = event.target.checked;
        setAllSelected(isChecked);
        setSelectedWishItems(isChecked ? [...wishlist] : []);
    };

    if (loading) {
        return <div className="loader">
            <Loading/>
        </div>
    }

    if (wishlist.length === 0) {
        return <div className="loader">
            No items in wishlist
        </div>
    }

    return (
        !loading && wishlist.length > 0 && <div className="xtable xtable_wishlist">
            <div className="xtable__tr">
                <div className="xtable__th">
                    <label className="radiocheck" onClick={e => e.stopPropagation()}>
                        <input 
                            type="checkbox" 
                            className="radiocheck__input"
                            checked={allSelected}
                            onChange={toggleAllItems}
                        />
                        <span className="radiocheck__text"></span>
                    </label>
                </div>
                <div className="xtable__th">
                </div>
                <div className="xtable__th">
                    Preview
                </div>
                <div className="xtable__th">
                    Video Story Name
                </div>
                <div className="xtable__th">
                    Price
                </div>
                <div className="xtable__th">
                    Date Added
                </div>
                <div className="xtable__th">
                    Status
                </div>
            </div>
            {wishlist.map((wishItem, index) => {
                const {product, addedAt} = wishItem;

                return <div className="xtable__tr" key={index}>
                    <div className="xtable__td">
                        <label className="radiocheck" onClick={e => e.stopPropagation()}>
                            <input 
                                type="checkbox" 
                                className="radiocheck__input" 
                                checked={selectedWishItems.some(item => item.product.id === wishItem.product.id)}
                                onChange={(ev) => onWishItemCheckboxChange(ev, wishItem)}
                            />
                            <span className="radiocheck__text"></span>
                        </label>
                    </div>
                    <div className="xtable__td">
                        <button className="btn-remove"
                                onClick={() => {
                                    removeFromWishlist(product)
                                }}
                                type="button" title="Remove">
                            <svg role="img" className="btn-remove__ico">
                                <use href="./img/sprite.svg#remove"></use>
                            </svg>
                        </button>
                    </div>

                    <div className="xtable__td">
                        <Link to={`/catalog/product/${product?.slug}`} className="xtable__preview">
                            {product?.images.length > 0 && <picture className="xtable__preview-pic">
                                <img className="xtable__preview-pic-img" src={`/api/media/${product?.images[0].image}?s=s`} alt={product?.name} loading="lazy"/>
                            </picture>}
                        </Link>
                    </div>
                    <div className="xtable__td">
                        <a href="#" className="xtable__name">{product.name}</a>
                    </div>
                    <div className="xtable__td">
                        ${product.price}
                    </div>
                    <div className="xtable__td">
                        {formatDate(new Date(addedAt))}
                    </div>
                    <div className="xtable__td">
                        <div className={`xtable__status m-${product.isAvailable ? 'available' : 'notavailable'}`}>
                            {product.isAvailable ? 'Available' : 'Not available'}
                        </div>

                    </div>
                </div>
            })}

            <div className="xtable__tr">
                <div className="xtable__td xtable__btns">
                    <div className="xtable__btns-group">
                        <button className="btn btn_alt btn_lg"

                                type="button" onClick={removeUnavailable}>REMOVE ALL</button>
                        <button className="btn btn_alt btn_lg"
                                disabled={wishlist.filter(v=>!v.product.isAvailable).length === 0}
                                type="button" onClick={removeAll}>REMOVE ALL NOT AVAILABLE</button>
                    </div>
                    <div className="xtable__btns-group">
                        <button 
                            className="btn btn_cta btn_lg" 
                            disabled={selectedWishItems.length === 0} 
                            type="button"
                            onClick={addSelectedToCart}
                        >
                            ADD SELECTED TO CART
                        </button>
                        <button 
                            className="btn btn_cta btn_lg" 
                            type="button"
                            onClick={addAllToCart}
                        >
                            ADD ALL TO CART
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default WishListTable;